import { useMemo } from 'react';
import { Bar } from 'react-chartjs-2';
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  LinearScale,
  Tooltip,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip);

export type Granularity = 'hour' | 'day' | 'week' | 'month';

export function ActivityChart({
  buckets,
  granularity,
}: {
  buckets: { t: string; c: number }[];
  granularity: Granularity;
}) {
  const { data, options } = useMemo(() => {
    const css = getComputedStyle(document.documentElement);
    const tickColor = css.getPropertyValue('--color-text-muted').trim() || '#888';
    const primary = css.getPropertyValue('--color-primary').trim() || '#3b82f6';
    return {
      data: {
        labels: buckets.map((b) => formatLabel(b.t, granularity)),
        datasets: [{ data: buckets.map((b) => b.c), backgroundColor: primary }],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false }, tooltip: { enabled: true } },
        scales: {
          x: { ticks: { color: tickColor, maxRotation: 0, autoSkip: true } },
          y: { ticks: { color: tickColor, precision: 0 }, beginAtZero: true },
        },
      } as const,
    };
  }, [buckets, granularity]);

  return (
    <div className="h-40">
      <Bar data={data} options={options} />
    </div>
  );
}

function formatLabel(iso: string, gran: Granularity): string {
  const d = new Date(iso);
  if (gran === 'hour') return `${String(d.getHours()).padStart(2, '0')}:00`;
  if (gran === 'month') {
    return d.toLocaleDateString('ru-RU', { month: 'short', year: '2-digit' });
  }
  // day, week — both render as "DD MMM"
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: 'short' });
}
